import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Table,
  Button,
  message,
  Typography,
  Card,
  Modal,
  Row,
  Col,
  Statistic,
  Input,
  Tag,
  Space,
} from "antd";
import {
  DollarOutlined,
  WarningOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import api from "../../../api/axios";
import { AuthContext } from "../../../context/AuthContext";

const { Title } = Typography;
const { Search } = Input;

const CollectCredit = () => {
  const [creditSales, setCreditSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [messageApi, contextHolder] = message.useMessage();
  const { user } = useContext(AuthContext);
const isMarketing = user?.role === 'marketing'; // 👈 ONLY THIS USER
  // ---------------- FETCH ----------------
  const fetchCreditSales = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/credit/pending");
      setCreditSales(res.data || []);
    } catch (err) {
      messageApi.error("Failed to load credit sales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditSales();
  }, []);

  // ---------------- REPORT CALCULATIONS ----------------

  const report = useMemo(() => {
    let totalPending = 0;
    let overdueCount = 0;
    const customerMap = {};

    creditSales.forEach((sale) => {
      totalPending += sale.grandTotal;

      const days =
        (new Date() - new Date(sale.createdAt)) / (1000 * 60 * 60 * 24);

      if (days > 30) overdueCount++;

      const name = sale.customer?.name || "Unknown";
      if (!customerMap[name]) customerMap[name] = 0;
      customerMap[name] += sale.grandTotal;
    });

    const topCustomer = Object.entries(customerMap).sort(
      (a, b) => b[1] - a[1]
    )[0];

    return {
      totalPending,
      totalTransactions: creditSales.length,
      overdueCount,
      topCustomer: topCustomer
        ? { name: topCustomer[0], amount: topCustomer[1] }
        : null,
    };
  }, [creditSales]);
// ---------------- SHOP SUMMARY ----------------

const shopSummary = useMemo(() => {
  const map = {};

  creditSales.forEach((sale) => {
    const shopName = sale.shop?.name || "Unknown Shop";

    if (!map[shopName]) {
      map[shopName] = {
        totalAmount: 0,
        totalTransactions: 0,
        overdue: 0,
      };
    }

    map[shopName].totalAmount += sale.grandTotal;
    map[shopName].totalTransactions += 1;

    const days =
      (new Date() - new Date(sale.createdAt)) /
      (1000 * 60 * 60 * 24);

    if (days > 30) {
      map[shopName].overdue += 1;
    }
  });

  // convert object to array & sort descending by amount
  return Object.entries(map)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.totalAmount - a.totalAmount);

}, [creditSales]);

  // ---------------- COLLECT ----------------

  const handleCollect = async (saleId) => {
    try {
      await api.post(`/api/credit/collect/${saleId}`);
      message.success("Credit collected successfully");
      fetchCreditSales();
    } catch(err) {
      console.error(err); 
      message.error("Failed to collect credit");
    }
  };

  // ---------------- SEARCH FILTER ----------------

  const filteredData = creditSales.filter((sale) =>
    sale.customer?.name
      ?.toLowerCase()
      .includes(searchText.toLowerCase())
  );

  // ---------------- TABLE COLUMNS ----------------

  const columns = [
    {
      title: "Ref No",
      dataIndex: "id",
    },
     {
      title: "Shop",
      render: (_, record) => record.shop?.name || "-",
    },
    {
      title: "Customer",
      render: (_, record) => record.customer?.name || "-",
    },
    {
      title: "Grand Total",
      dataIndex: "grandTotal",
      render: (val) => `$${val?.toFixed(2)}`,
    },
    {
  title: "Created At",
  dataIndex: "createdAt",
  key: "createdAt",
  render: (value) => {
    return value ? new Date(value).toLocaleDateString() : "-";
  },
},
{
  title: "Created By",
  key: "createdBy",
  render: (_, record) => {
    return record.userId || "-";
  },
},
    {
      title: "Aging",
      render: (_, record) => {
        const days = Math.floor(
          (new Date() - new Date(record.createdAt)) /
            (1000 * 60 * 60 * 24)
        );

        if (days <= 15)
          return <Tag color="green">{days} Days</Tag>;
        if (days <= 30)
          return <Tag color="orange">{days} Days</Tag>;

        return <Tag color="red">{days} Days</Tag>;
      },
    },
    {
      title: "Action",
      render: (_, record) => (
        <Button
          type="primary"
          danger={
            (new Date() - new Date(record.createdAt)) /
              (1000 * 60 * 60 * 24) >
            30
          }
          disabled={isMarketing || record.isCollected}
          onClick={() => handleCollect(record.id)}
        >
          Collect
        </Button>
      ),
    },
  ];

  // ---------------- VIEW ITEMS ----------------

  const handleViewItems = (sale) => {
    setSelectedSale(sale);
    setModalVisible(true);
  };

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}

      <Title level={3}>Credit Control Dashboard</Title>

      {/* REPORT CARDS */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Pending Credit"
              value={report.totalPending}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={report.totalTransactions}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue (30+ Days)"
              value={report.overdueCount}
              valueStyle={{ color: "red" }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card>
            <Statistic
              title="Top Creditor"
              value={report.topCustomer?.name || "-"}
              suffix={
                report.topCustomer
                  ? `$${report.topCustomer.amount.toFixed(2)}`
                  : ""
              }
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>
{/* SHOP CREDIT SUMMARY */}
<Card
  title="Credit Summary by Shop"
  style={{ marginBottom: 20 }}
>
  <Row gutter={[16, 16]}>
    {shopSummary.map((shop) => (
      <Col xs={24} sm={12} md={8} lg={6} key={shop.name}>
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Title level={5}>{shop.name}</Title>

          <Statistic
            title="Total Credit"
            value={shop.totalAmount}
            precision={2}
            prefix="$"
          />

          <div style={{ marginTop: 10 }}>
            <Tag color="blue">
              {shop.totalTransactions} Transactions
            </Tag>

            {shop.overdue > 0 && (
              <Tag color="red">
                {shop.overdue} Overdue
              </Tag>
            )}
          </div>
        </Card>
      </Col>
    ))}
  </Row>
</Card>

      {/* SEARCH */}
      <Card style={{ marginBottom: 16 }}>
        <Search
          placeholder="Search by customer name..."
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Card>

      {/* TABLE */}
      <Card>
        <Table
          columns={[
            ...columns,
            {
              title: "Items",
              render: (_, record) => (
                <Button onClick={() => handleViewItems(record)}>
                  View Items
                </Button>
              ),
            },
          ]}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          rowClassName={(record) => {
            const days =
              (new Date() - new Date(record.createdAt)) /
              (1000 * 60 * 60 * 24);
            return days > 30 ? "overdue-row" : "";
          }}
        />
      </Card>

      {/* MODAL */}
      <Modal
        title="Sales Items"
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={800}
      >
        {selectedSale && (
          <Table
            dataSource={selectedSale.items}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: "Part Name",
                dataIndex: ["sparepart", "partName"],
              },
              {
                title: "Quantity",
                dataIndex: "quantity",
              },
              {
                title: "Unit Price",
                dataIndex: "unitPrice",
                render: (v) => `$${v?.toFixed(2)}`,
              },
              {
                title: "Line Total",
                dataIndex: "lineTotal",
                render: (v) => `$${v?.toFixed(2)}`,
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default CollectCredit;
